import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Smartphone, 
  Camera, 
  MapPin, 
  Bell, 
  Download,
  Wifi,
  WifiOff,
  Battery,
  Signal,
  X
} from 'lucide-react';
import { useNativeFeatures } from '@/hooks/useNativeFeatures';
import { usePWA } from '@/hooks/usePWA';
import { useOfflineStorage } from '@/hooks/useOfflineStorage';
import { toast } from 'sonner';

export const EnhancedMobileApp: React.FC = () => {
  const { 
    isNative, 
    permissions, 
    takePicture, 
    getCurrentLocation, 
    showLocalNotification,
    initializeNativeFeatures 
  } = useNativeFeatures();

  const { 
    isInstallable, 
    isInstalled, 
    isOnline, 
    showInstallPrompt, 
    installApp, 
    dismissInstallPrompt 
  } = usePWA();

  const { 
    pendingActions, 
    addOfflineAction, 
    saveDataOffline, 
    getDataOffline 
  } = useOfflineStorage();

  useEffect(() => {
    if (isNative) {
      initializeNativeFeatures();
    }
  }, [isNative, initializeNativeFeatures]);

  const handleTakePicture = async () => {
    try {
      const imageUrl = await takePicture();
      if (imageUrl) {
        toast.success('Picture taken successfully');
        console.log('Image URL:', imageUrl);
      }
    } catch (error) {
      toast.error('Failed to take picture');
      console.error('Camera error:', error);
    }
  };

  const handleGetLocation = async () => {
    try {
      const location = await getCurrentLocation();
      toast.success(`Location: ${location.latitude}, ${location.longitude}`);
      console.log('Location:', location);
    } catch (error) {
      toast.error('Failed to get location');
      console.error('Location error:', error);
    }
  };

  const handleShowNotification = async () => {
    try {
      await showLocalNotification(
        'Test Notification',
        'This is a test notification from the mobile app'
      );
      toast.success('Notification scheduled');
    } catch (error) {
      toast.error('Failed to show notification');
      console.error('Notification error:', error);
    }
  };

  const handleOfflineAction = async () => {
    await addOfflineAction({
      type: 'create',
      endpoint: '/api/test',
      data: { message: 'Test offline action', timestamp: Date.now() }
    });
    toast.success('Action queued for when online');
  };

  const handleSaveOfflineData = async () => {
    await saveDataOffline('test-data', {
      id: Date.now(),
      message: 'Test offline data',
      timestamp: new Date().toISOString()
    });
    toast.success('Data saved offline');
  };

  return (
    <div className="space-y-6">
      {/* PWA Install Prompt */}
      {showInstallPrompt && !isInstalled && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <Download className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">Install App</p>
                <p className="text-sm text-blue-700">Get the best experience with our mobile app</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={installApp}>
                Install
              </Button>
              <Button size="sm" variant="ghost" onClick={dismissInstallPrompt}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Platform</CardTitle>
            <Smartphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isNative ? 'Native' : 'Web'}
            </div>
            <p className="text-xs text-muted-foreground">
              {isInstalled ? 'Installed' : 'Not installed'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Connection</CardTitle>
            {isOnline ? (
              <Wifi className="h-4 w-4 text-green-600" />
            ) : (
              <WifiOff className="h-4 w-4 text-red-600" />
            )}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isOnline ? 'Online' : 'Offline'}
            </div>
            <p className="text-xs text-muted-foreground">
              {Array.isArray(pendingActions) ? pendingActions.length : 0} pending actions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Camera</CardTitle>
            <Camera className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <Badge variant={permissions.camera ? 'default' : 'secondary'}>
                {permissions.camera ? 'Granted' : 'Denied'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Location</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <Badge variant={permissions.location ? 'default' : 'secondary'}>
                {permissions.location ? 'Granted' : 'Denied'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feature Tabs */}
      <Tabs defaultValue="native" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="native">Native Features</TabsTrigger>
          <TabsTrigger value="pwa">PWA Features</TabsTrigger>
          <TabsTrigger value="offline">Offline Capabilities</TabsTrigger>
        </TabsList>

        <TabsContent value="native" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Native Device Features</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                <Button 
                  onClick={handleTakePicture}
                  disabled={!permissions.camera}
                  className="justify-start"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Take Picture
                </Button>
                
                <Button 
                  onClick={handleGetLocation}
                  disabled={!permissions.location}
                  className="justify-start"
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  Get Current Location
                </Button>
                
                <Button 
                  onClick={handleShowNotification}
                  disabled={!permissions.notifications}
                  className="justify-start"
                >
                  <Bell className="h-4 w-4 mr-2" />
                  Send Test Notification
                </Button>
              </div>

              {!isNative && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Native features are only available when the app is installed as a native app.
                    Install the app to access camera, location, and push notifications.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pwa" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Progressive Web App Features</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">App Installation</p>
                    <p className="text-sm text-muted-foreground">
                      {isInstalled ? 'App is installed' : 'App can be installed'}
                    </p>
                  </div>
                  {isInstallable && !isInstalled && (
                    <Button onClick={installApp}>
                      <Download className="h-4 w-4 mr-2" />
                      Install
                    </Button>
                  )}
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Offline Support</p>
                    <p className="text-sm text-muted-foreground">
                      App works without internet connection
                    </p>
                  </div>
                  <Badge variant={isOnline ? 'default' : 'secondary'}>
                    {isOnline ? 'Online' : 'Offline'}
                  </Badge>
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Push Notifications</p>
                    <p className="text-sm text-muted-foreground">
                      Receive notifications even when app is closed
                    </p>
                  </div>
                  <Badge variant={permissions.notifications ? 'default' : 'secondary'}>
                    {permissions.notifications ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="offline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Offline Capabilities</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                <Button onClick={handleOfflineAction} className="justify-start">
                  Queue Offline Action
                </Button>
                
                <Button onClick={handleSaveOfflineData} className="justify-start">
                  Save Data Offline
                </Button>
              </div>

              {Array.isArray(pendingActions) && pendingActions.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">Pending Actions ({pendingActions.length})</h4>
                  {pendingActions.slice(0, 3).map((action) => (
                    <div key={action.id} className="p-2 bg-muted rounded text-sm">
                      <div className="flex justify-between">
                        <span>{action.type}</span>
                        <span className="text-muted-foreground">
                          {new Date(action.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Actions performed while offline will be automatically synced when connection is restored.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};